import { useState } from 'react';
import { OrgUnitModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { UserKeys, createOrgUnit } from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { Controller, useForm } from 'react-hook-form';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface AddOrgUnitDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  orgUnits: OrgUnitModel[];
  selectedOrgUnitId: string;
}

export default function AddOrgUnitDialog({
  open,
  handleClose,
  changeLogRoles,
  orgUnits,
  selectedOrgUnitId,
}: AddOrgUnitDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<OrgUnitModel>) =>
      createOrgUnit(
        {
          ...data,
          parentOrgUnitId: orgUnits.find(
            (org) => org.orgUnitPath === data.parentOrgUnitPath
          )?.orgUnitId,
        },
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Organizational unit created successfully!',
      });

      // This invalidates all autocompletes so they get the latest changes
      queryClient.invalidateQueries({
        queryKey: [UserKeys.OrgUnitsFromGoogle],
      });
      // This invalidates the tree so it gets the latest changes
      queryClient.invalidateQueries({ queryKey: [UserKeys.OrgUnitModels] });

      reset();
      handleClose();
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the orgazational unit. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.organizational_units.create_org_unit, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.organizationalUnits,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    values: {
      name: '',
      description: '',
      parentOrgUnitPath: selectedOrgUnitId,
      blockInheritance: false,
    },
  });

  function onSubmit(data: Partial<OrgUnitModel>) {
    if (isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.AddOrgUnit.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title={`Create Organizational Unit`}
      dialogContent={
        <Stack
          gap={1}
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          <Controller
            name={'name'}
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'Organizational Unit',
                }}
                InputLabelProps={{
                  htmlFor: 'Organizational Unit',
                }}
                label="Organizational Unit"
                fullWidth
                autoComplete="off"
                autoFocus
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Controller
            name={'description'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'description',
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Description"
                fullWidth
                autoComplete="off"
              />
            )}
          />
          <Controller
            name="parentOrgUnitPath"
            control={control}
            render={({ field }) => (
              <Autocomplete
                id="org-unit-autocomplete"
                value={orgUnits.find((orgUnit) => {
                  if (!field.value) {
                    return orgUnit.orgUnitPath === '/';
                  } else {
                    return orgUnit.orgUnitPath === field.value;
                  }
                })}
                onChange={(_event, newValue) => {
                  setValue('parentOrgUnitPath', newValue?.orgUnitPath || '/', {
                    shouldDirty: true,
                  });
                }}
                options={orgUnits.sort((a, b) =>
                  a.orgUnitPath.localeCompare(b.orgUnitPath)
                )}
                getOptionLabel={(option) => option.orgUnitPath}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Organization Unit"
                    sx={{ minWidth: 400, mt: 3 }}
                  />
                )}
              />
            )}
          />

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newOrgUnit: Partial<OrgUnitModel> = getValues();
              mutate(newOrgUnit);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'CREATE ORG UNIT',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
